import { call, put, select, takeEvery } from "redux-saga/effects";

import { getCustomization } from "@multiweb/customization";
import {
    bridge,
    createSetReactConnectorMultiselectModeAction,
} from "@multiweb/reactCore/bridge";
import { createLoadLocalizationKeysAction } from "@multiweb/reactCore/i18n";
import { EMPTY_ARRAY } from "@multiweb/reactUtils";
import { CancelError, CancellationToken, CancellationTokenSource, IWorkflowMenuItem } from "@multiweb/types/multiweb";
import { MutableTypedObject, RecordType, SagaWithReturnType, SagaYieldReturn } from "@multiweb/types/react";
import {
    createLoadAttributeInfoErrorAction,
    createLoadAttributeInfoSuccessAction,
    createLoadDataErrorAction,
    createLoadDataSuccessAction,
    createLoadWorkflowErrorAction,
    createLoadWorkflowSuccessAction,
    createRefreshAction,
    createRefreshErrorAction,
    createRefreshSuccessAction,
    createUpdateRecordActionsAction,
    createUpdateRecordActionsSuccessAction,
} from "./actions";
import {
    createFetchActionsLocalizationsKey,
    createFetchAttributesLocalizationsKey,
    EXECUTE_WORKFLOW_ACTION,
    GET_WORKFLOW_ACTIONS_REQUEST,
    INIT_DATASOURCE,
    LOAD_DATA,
    REFRESH_DATA,
    SET_DATASOURCE_MULTISELECT_MODE,
    SET_ORDER_BY,
    TOGGLE_ORDER_BY,
    UPDATE_FULLTEXT_CONDITION,
    UPDATE_MASTER_FRAME_CONDITION,
    UPDATE_RECORD_ACTIONS,
    UPDATE_RECORD_ACTIONS_SUCCESS,
} from "./constants";
import { getDataSourceInstance, getLoadingCondition, getWorkflowActionById } from "./selectors";
import {
    DisabledLoadingError,
    IDataSourceMappedAction,
    IDataSourceState,
    IExecuteWorkflowAction,
    IInitDataSourceAction,
    ILoadDataAction,
    ILoadWorkflowActionsAction,
    IRefreshDataAction,
    ISetDatasourceMultiselectModeAction,
    ISetOrderByAction,
    IToggleOrderByAction,
    IUpdateFulltextDataSourceCondition,
    IUpdateMasterFrameDataSourceCondition,
    IUpdateRecordActionsAction,
    IUpdateRecordActionsSuccessAction,
} from "./types";
import { getParameters, mapApiDataToStateData } from "./utils";

const {
    createObjectList,
    errorHandler,
    getAttributeInfo,
    getDataReaderClient,
    getReactConnectorRowActions,
    objectsWFMenu,
    objectWFActionExecuteWithoutInstance,
    resetRefreshTimeout,
} = bridge;

const cancellationTokens: MutableTypedObject<CancellationTokenSource[]> = {};

export const saga: SagaWithReturnType = function* dataSourceSaga() {
    yield takeEvery(INIT_DATASOURCE, initDataSourceSaga);
    yield takeEvery(LOAD_DATA, loadSaga);
    yield takeEvery(REFRESH_DATA, refreshSaga);
    yield takeEvery([SET_ORDER_BY, TOGGLE_ORDER_BY], orderBySaga);
    yield takeEvery([UPDATE_FULLTEXT_CONDITION, UPDATE_MASTER_FRAME_CONDITION], updateDataSourceConditionSaga);
    yield takeEvery(UPDATE_RECORD_ACTIONS, updateRecordActionsSaga);
    yield takeEvery(UPDATE_RECORD_ACTIONS_SUCCESS, updateRecordActionsSuccessSaga);
    yield takeEvery(SET_DATASOURCE_MULTISELECT_MODE, setDatasourceMultiselectSaga);
    yield takeEvery(GET_WORKFLOW_ACTIONS_REQUEST, getWorkflowActionsSaga);
    yield takeEvery(EXECUTE_WORKFLOW_ACTION, executeWorkflowActionSaga);
};

export const initDataSourceSaga: SagaWithReturnType<[IInitDataSourceAction]> = function* initDataSourceSaga(action) {
    const { dataSourceKey } = action.meta;
    const { objectName, attributes } = action.payload.config;
    const isPersistent = action.payload.config.isPersistent;
    const attributeInfo = action.payload.attributeInfo;

    const locKeys: string[] = attributes.map((attribute) => attribute.locKey || "");
    yield put(createLoadLocalizationKeysAction(locKeys, createFetchAttributesLocalizationsKey(dataSourceKey), true));

    if (isPersistent) {
        try {
            const attributeInfoArray: SagaYieldReturn<typeof getAttributeInfo> = yield call(
                getAttributeInfo,
                objectName,
                attributes.map((attribute) => attribute.attributeName),
            );
            yield put(createLoadAttributeInfoSuccessAction(dataSourceKey, attributeInfoArray));
        } catch (error) {
            yield call(errorHandler, error);
            yield put(createLoadAttributeInfoErrorAction(dataSourceKey, error instanceof Error ? error.message : undefined));
        }
    } else {
        yield put(createLoadAttributeInfoSuccessAction(dataSourceKey, attributeInfo));
    }
};

export const updateDataSourceConditionSaga: SagaWithReturnType<
    [IUpdateFulltextDataSourceCondition | IUpdateMasterFrameDataSourceCondition]
> = function* updateDataSourceConditionSaga(action) {
    const { dataSourceKey } = action.meta;
    const { refreshOnChange } = action.payload;
    if (refreshOnChange) {
        yield put(createRefreshAction(dataSourceKey));
    }
};

/**
 * Vytvoří nový cancellation token a uloží jej do mapy pod indexem dataSourceKey.
 * Ostatní tokeny pro daný dataSourceKey jsou stornovány.
 * @param action
 */
export const createCancellationTokenSaga: SagaWithReturnType<
    [IDataSourceMappedAction],
    CancellationToken
// eslint-disable-next-line require-yield
> = function* createCancellationTokenSaga(action) {
    const { dataSourceKey } = action.meta;

    const tokenSource = new CancellationTokenSource();
    if (!cancellationTokens[dataSourceKey]) {
        cancellationTokens[dataSourceKey] = [];
    }
    cancellationTokens[dataSourceKey].forEach((tokenSource) => {
        if (!tokenSource.token.cancellationRequested) {
            tokenSource.cancel();
        }
    });
    cancellationTokens[dataSourceKey].push(tokenSource);

    return tokenSource.token;
};

/**
 * Smaže cancellation tokeny pro daný dataSourceKey.
 * @param action
 */
export const clearCancellationTokenSaga: SagaWithReturnType<
    [IDataSourceMappedAction]
// eslint-disable-next-line require-yield
> = function* clearCancellationTokenSaga(action) {
    const { dataSourceKey } = action.meta;

    cancellationTokens[dataSourceKey] = [];
};

/**
 * Vrací dataSource instanci podle dataSourceKey z akce.
 * @param action
 */
export const getDataSourceSaga: SagaWithReturnType<
    [IDataSourceMappedAction],
    IDataSourceState
> = function* getDataSourceSaga(action) {
    const { dataSourceKey } = action.meta;

    const dataSourceInstance: ReturnType<typeof getDataSourceInstance> = yield select(
        getDataSourceInstance,
        dataSourceKey,
    );
    if (!dataSourceInstance) {
        throw new Error(`[DataSource] DataSource is not initialized: dataSourceKey=${dataSourceKey}`);
    }
    const { config }: IDataSourceState = dataSourceInstance;
    if (!config) {
        throw new Error("[DataSource] DataSource does not have configuration.");
    }

    return dataSourceInstance;
};

/**
 * Vrací načtená data.
 * @param action
 * @param startIndex Index, od kterého se mají data načítat.
 * @param fetchCount Požadovaný počet načítaných dat.
 */
export const loadDataSaga: SagaWithReturnType<
    [string, IDataSourceMappedAction, number, number],
    RecordType[]
> = function* loadDataSaga(dataSourceKey, action, startIndex, fetchCount) {
    const cancellationToken: SagaYieldReturn<typeof createCancellationTokenSaga> = yield call(
        createCancellationTokenSaga,
        action,
    );

    const { config, orderBy, conditions: { conditionParams }, loading: { disabledLoading } }: SagaYieldReturn<typeof getDataSourceSaga> = yield call(
        getDataSourceSaga,
        action,
    );
    if (!config) {
        return EMPTY_ARRAY as RecordType[];
    }
    if (disabledLoading) {
        throw new DisabledLoadingError(dataSourceKey);
    }

    //@ts-ignore // TODO: zrušit ignore v #33783
    // eslint-disable-next-line unused-imports/no-unused-vars
    const { objectName, attributeNames, fullLengthAttributes, recordIdKey, formContext } = config;

    const loadingConditions: ReturnType<typeof getLoadingCondition> = yield select(getLoadingCondition, dataSourceKey);

    const parameters: ReturnType<typeof getParameters> = yield call(
        getParameters,
        startIndex,
        fetchCount,
        recordIdKey,
        orderBy,
        conditionParams,
    );
    const dataReaderClient: SagaYieldReturn<typeof getDataReaderClient> = yield call(getDataReaderClient);

    const apiData: SagaYieldReturn<typeof dataReaderClient.getDataRowsWithParams> = yield call(
        //@ts-ignore // TODO: zrušit ignore v #33783
        dataReaderClient.getDataRowsWithParams.bind(dataReaderClient),
        objectName,
        attributeNames,
        loadingConditions ?? "",
        parameters,
        fullLengthAttributes,
        undefined, // TODO: doplnit requestContext v #33783
        cancellationToken,
    );
    const records: SagaYieldReturn<typeof mapApiDataToStateData> = yield call(mapApiDataToStateData, apiData);
    return records;
};

/**
 * Sága obstarávající načítání nových dat.
 * @param action
 */
export const loadSaga: SagaWithReturnType<[ILoadDataAction]> = function* loadSaga(action) {
    const { dataSourceKey } = action.meta;

    try {
        const { fetchCount } = action.payload;
        const { config, loading: { startIndex } }: SagaYieldReturn<typeof getDataSourceSaga> = yield call(getDataSourceSaga, action);

        const callMethod = getCustomization().core.datasource.getMethodByDataSource(LOAD_DATA, config?.customDataSourceId, true);

        if (callMethod) {
            const callMethodArgs = config?.customMethodsData?.[LOAD_DATA]?.data ?? [];

            const data: RecordType[] = yield call(callMethod, ...callMethodArgs);

            yield put(createLoadDataSuccessAction(dataSourceKey, data));
        } else {
            const data: ReturnType<typeof mapApiDataToStateData> = yield call(
                loadDataSaga,
                dataSourceKey,
                action,
                startIndex,
                fetchCount,
            );

            yield put(createLoadDataSuccessAction(dataSourceKey, data));
        }
        yield call(resetRefreshTimeout, dataSourceKey);
    } catch (error) {
        if (error instanceof CancelError) {
            return;
        } else if (!(error instanceof DisabledLoadingError)) {
            yield call(errorHandler, error);
        }
        yield put(createLoadDataErrorAction(dataSourceKey, error instanceof Error ? error.message : undefined));
    } finally {
        yield call(clearCancellationTokenSaga, action);
    }
};

/**
 * Sága obstarávající aktualizaci dat. Tato sága by se neměla volat přímo, ale vždy jen přes refresh akci.
 * Refresh akce totiž zajišťuje správné vyčištění stavu a nastavení načítacích příznaků.
 * @param action
 */
export const refreshSaga: SagaWithReturnType<[IRefreshDataAction]> = function* refreshSaga(action) {
    const { dataSourceKey } = action.meta;

    try {
        const { config, loading: { fetchingCount } }: SagaYieldReturn<typeof getDataSourceSaga> = yield call(getDataSourceSaga, action);

        const callMethod = getCustomization().core.datasource.getMethodByDataSource(REFRESH_DATA, config?.customDataSourceId, true);

        if (callMethod) {
            const callMethodArgs = config?.customMethodsData?.[REFRESH_DATA]?.data ?? [];
            const data: RecordType[] = yield call(callMethod, ...callMethodArgs);

            yield put(createLoadDataSuccessAction(dataSourceKey, data));
        } else {
            const refreshedData: ReturnType<typeof mapApiDataToStateData> = yield call(
                loadDataSaga,
                dataSourceKey,
                action,
                0,
                fetchingCount,
            );
            yield put(createRefreshSuccessAction(dataSourceKey, refreshedData));
        }
        yield call(resetRefreshTimeout, dataSourceKey);
    } catch (error) {
        if (error instanceof CancelError) {
            return;
        } else if (!(error instanceof DisabledLoadingError)) {
            yield call(errorHandler, error);
        }
        yield put(createRefreshErrorAction(dataSourceKey, error instanceof Error ? error.message : undefined));
    } finally {
        yield call(clearCancellationTokenSaga, action);
    }
};

/**
 * Sága obstarávající změnu v řazení dat.
 * @param action
 */
export const orderBySaga: SagaWithReturnType<[ISetOrderByAction | IToggleOrderByAction]> = function* orderBySaga(
    action,
) {
    const { dataSourceKey } = action.meta;

    yield put(createRefreshAction(dataSourceKey));
};

/**
 * Aktualizuje akce pro záznam z toolbaru v connectoru.
 */
export const updateRecordActionsSaga: SagaWithReturnType<[IUpdateRecordActionsAction]> = function* updateRecordActionsSaga(
    action,
) {
    const { dataSourceKey } = action.meta;
    const actionsArray: SagaYieldReturn<typeof getReactConnectorRowActions> = yield call(
        getReactConnectorRowActions,
        dataSourceKey,
    );
    yield put(createUpdateRecordActionsSuccessAction(dataSourceKey, actionsArray));
};

/**
 * Aktualizuje akce pro záznam z toolbaru v connectoru.
 */
export const updateRecordActionsSuccessSaga: SagaWithReturnType<[IUpdateRecordActionsSuccessAction]> = function* updateRecordActionsSaga(
    action,
) {
    const { dataSourceKey } = action.meta;
    const actions = action.payload.actions;
    const actionsLocKeys = actions.map((action) => action.locKey).filter((locKey): locKey is string => !!locKey); // přetypování nutné, protože TS neodvodí správný typ.
    if (actionsLocKeys.length > 0) {
        yield put(createLoadLocalizationKeysAction(actionsLocKeys, createFetchActionsLocalizationsKey(dataSourceKey), true));
    }
};

/**
 * Nastaví korektní multiselect mode connectoru v MultiWebu a aktualizuje record akce (mění se jejich viditelnost).
 */
export const setDatasourceMultiselectSaga: SagaWithReturnType<[ISetDatasourceMultiselectModeAction]> = function* setDatasourceMultiselectSaga(
    action,
) {
    const { dataSourceKey } = action.meta;
    yield put(createSetReactConnectorMultiselectModeAction(dataSourceKey, action.payload.multiselectMode));
    yield put(createUpdateRecordActionsAction(dataSourceKey));
};

/**
 * Získá Workflow akce záznamu a následné uložení do redux store.
 */
export const getWorkflowActionsSaga: SagaWithReturnType<[ILoadWorkflowActionsAction]> = function* getWorkflowActionsSaga(
    action,
) {
    const { dataSourceKey } = action.meta;
    try {
        const { config }: SagaYieldReturn<typeof getDataSourceSaga> = yield call(
            getDataSourceSaga,
            action,
        );
        const { objectName } = config;
        const objectList = yield call(createObjectList);
        objectList.className = objectName;
        objectList?.add({ className: objectName, id: action.payload.recordId });
        const result: SagaYieldReturn<typeof objectsWFMenu> = yield call(objectsWFMenu, objectList);
        yield put(createLoadWorkflowSuccessAction(dataSourceKey, result));
    } catch (error) {
        const err = error as Error;
        yield call(errorHandler, err);
        yield put(createLoadWorkflowErrorAction(dataSourceKey, err.message));
    }
};

/**
 * Provedení workflow akce.
 */
export const executeWorkflowActionSaga: SagaWithReturnType<[IExecuteWorkflowAction]> = function* executeWorkflowActionSaga(
    action,
) {
    const { dataSourceKey } = action.meta;

    try {
        const { config }: SagaYieldReturn<typeof getDataSourceSaga> = yield call(
            getDataSourceSaga,
            action,
        );

        const workflowAction: IWorkflowMenuItem = yield select(getWorkflowActionById, { dataSourceKey, workflowActionId: action.payload.workflowActionId });
        yield call(
            objectWFActionExecuteWithoutInstance,
            [action.payload.recordId],
            config.objectName,
            workflowAction.ID,
            [],
        );
    } catch (error) {
        const err = error as Error;
        yield call(errorHandler, err);
    }
    yield put(createRefreshAction(dataSourceKey));
};
